import {
  AccessContext,
  EnumField,
  MoreEq,
  NumberField,
  RangeInc,
  Required,
  Schema,
  TextField
} from "lego-gui-lib";
import { LongTextField } from "lego-gui-lib/lib";
import {
  BooleanField,
  BooleanFieldWithBooleans
} from "../../../../common/common";
import { t } from "../../../../core/i18n/i18n";
import { mainStore } from "../../../../core/mainStore";
import { Auth } from "../../../../core/model/auth";
import { DestinationFK } from "../../../destination/schema";
import { HuntingGroupFK } from "../../../huntingGroup/schema";
import { IvrMenuFK } from "../../../ivrMenu/schema";
import {
  CustomerMessagesAnnouncementFKNotRequired,
  CustomerMessagesFK,
  CustomerMessagesFKNotRequired
} from "../../../message/schema";
import { SubTreeFK, TreeFK } from "../../schema";
import { StandardIVRDialOptions } from "../schema";
import { TreeBlockTypesWithLabels } from "../TreeBlockType";
import { NumericCodeMinDigitsToCollectValidator } from "./validators";

const ExtAppHttpMethods = [
  { label: t("hns:tree:treeBlock:extAppDest:get"), value: "GET" },
  { label: t("hns:tree:treeBlock:extAppDest:post"), value: "POST" }
];

const ReleaseReasons = [
  { label: t("hns:tree:treeBlock:releaseReasons:busy"), value: "BUSY" },
  { label: t("hns:tree:treeBlock:releaseReasons:rejected"), value: "REJECTED" }
];

export enum AdvancedConditionParamValue {
  NORMALIZED_A = "NORMALIZED_A",
  NORMALIZED_B = "NORMALIZED_B",
  SAN_EXTENSION = "SAN_EXTENSION",
  LAST_SIP_RESPONSE_CODE = "LAST_SIP_RESPONSE_CODE",
  INCOMING_XRN = "INCOMING_XRN"
}

const AdvancedConditionParams = [
  {
    label: t("hns:tree:treeBlock:advancedConditionsParams:normalizedA"),
    value: AdvancedConditionParamValue.NORMALIZED_A
  },
  {
    label: t("hns:tree:treeBlock:advancedConditionsParams:normalizedB"),
    value: AdvancedConditionParamValue.NORMALIZED_B
  },
  {
    label: t("hns:tree:treeBlock:advancedConditionsParams:sanExtension"),
    value: AdvancedConditionParamValue.SAN_EXTENSION
  },
  {
    label: t("hns:tree:treeBlock:advancedConditionsParams:lastSipResponseCode"),
    value: AdvancedConditionParamValue.LAST_SIP_RESPONSE_CODE
  },
  {
    label: t("hns:tree:treeBlock:advancedConditionsParams:incomingXrn"),
    value: AdvancedConditionParamValue.INCOMING_XRN
  }
];

/*
 * Abstract Tree Block schema with every available field listed
 * */
export const AbstractTreeBlockSchema = new Schema({
  primaryKey: ["id"],
  context: { auth: mainStore.auth },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "treeRef",
      type: TreeFK,
      readonly: true,
      title: t("hns:tree:domain")
    },
    {
      name: "isRoot",
      readonly: true,
      type: BooleanField(),
      title: t("hns:tree:treeBlock:isRoot")
    },
    {
      name: "type",
      readonly: true,
      type: EnumField(TreeBlockTypesWithLabels, Required),
      title: t("common:type")
    },
    {
      name: "destinationRef",
      type: DestinationFK,
      title: t("hns:tree:treeBlock:destination")
    },
    {
      name: "releaseReason",
      type: EnumField(ReleaseReasons, Required),
      title: t("hns:tree:treeBlock:releaseReason")
    },
    {
      name: "releaseResponseCode",
      type: TextField(RangeInc(300, 699), Required),
      title: t("hns:tree:treeBlock:releaseResponseCode")
    },
    {
      name: "advancedConditionParam",
      type: EnumField(AdvancedConditionParams, Required),
      title: t("hns:tree:treeBlock:advancedConditionsParams:param")
    },
    {
      name: "messageRef",
      type: CustomerMessagesFKNotRequired,
      title: t("hns:tree:treeBlock:message")
    },
    {
      name: "subtreeRef",
      type: SubTreeFK,
      title: t("hns:tree:treeBlock:subtree")
    },
    {
      name: "ivrMenuRef",
      type: IvrMenuFK,
      title: t("hns:tree:treeBlock:ivrMenu")
    },
    {
      name: "huntingGroupRef",
      type: HuntingGroupFK,
      title: t("hns:tree:treeBlock:huntingGroup")
    },
    {
      name: "extAppReqType",
      type: EnumField(ExtAppHttpMethods, Required),
      title: t("hns:tree:treeBlock:extAppDest:reqType")
    },
    {
      name: "extAppUrl",
      type: TextField(Required),
      title: t("hns:tree:treeBlock:extAppDest:url")
    },
    {
      name: "extAppParams",
      type: LongTextField(Required),
      title: t("hns:tree:treeBlock:extAppDest:params")
    },
    {
      name: "extAppRespXPath",
      type: TextField(Required),
      title: t("hns:tree:treeBlock:extAppDest:xPath")
    },
    {
      name: "counterName",
      type: TextField(Required),
      title: t("hns:tree:treeBlock:registrationCounterParams:counterName")
    },
    {
      name: "counterValue",
      type: NumberField,
      readonly: true,
      title: t("hns:tree:treeBlock:registrationCounterParams:counterValue")
    },
    {
      name: "messageToPlay",
      type: CustomerMessagesFK,
      title: t("hns:tree:treeBlock:numericCodeParams:messageToPlay")
    },
    {
      name: "repeatMessage",
      type: BooleanFieldWithBooleans(Required),
      title: t("hns:tree:treeBlock:standardIvrParams:repeatMessage")
    },
    {
      name: "repetitionMessageDigit",
      type: StandardIVRDialOptions(),
      title: t("hns:tree:treeBlock:standardIvrParams:repetitionMessageDigit"),
      if: (ctx: AccessContext) => ctx.data.repeatMessage === true
    },
    {
      name: "maxRepetitions",
      type: NumberField(RangeInc(0, 99), Required),
      title: t("hns:tree:treeBlock:standardIvrParams:maxRepetitions"),
      if: (ctx: AccessContext) => ctx.data.repeatMessage === true
    },
    {
      name: "minDigitsToCollect",
      type: NumberField(
        NumericCodeMinDigitsToCollectValidator,
        Required,
        RangeInc(0, 30)
      ),
      title: t("hns:tree:treeBlock:numericCodeParams:minDigitsToCollect"),
      onChange: (newValue, oldValue, formState) => {
        if (!formState.values["maxDigitsToCollect"]) {
          formState.values["maxDigitsToCollect"] = newValue;
        }
      }
    },
    {
      name: "maxDigitsToCollect",
      type: ctx =>
        NumberField(
          Required,
          RangeInc(ctx.data.minDigitsToCollect || 0, 30),
          MoreEq(ctx.data.minDigitsToCollect || 0)
        ),
      title: t("hns:tree:treeBlock:numericCodeParams:maxDigitsToCollect")
    },
    {
      name: "timeoutRetries",
      type: NumberField,
      title: t("hns:tree:treeBlock:standardIvrParams:timeoutRetries")
    },
    {
      name: "timeoutMessage",
      type: CustomerMessagesAnnouncementFKNotRequired,
      title: t("hns:tree:treeBlock:standardIvrParams:timeoutMessage")
    },
    {
      name: "errorRetries",
      type: NumberField,
      title: t("hns:tree:treeBlock:standardIvrParams:errorRetries")
    },
    {
      name: "errorMessage",
      type: CustomerMessagesAnnouncementFKNotRequired,
      title: t("hns:tree:treeBlock:standardIvrParams:errorMessage")
    }
  ]
});
